<svelte:options runes={false} />

<script lang="ts">
	// Formatting
	import { format } from 'svelte-i18n';
	// Stores
	import {
		selectraIsQualified,
		selectraQualification,
		selectraDetails,
		selectraPrices,
		removeSelectraQualification,
	} from 'src/stores/selectra';
	// Components
	import SelectraQualificationWizard from './SelectraQualificationWizard.svelte';
	// Design system components
	import {
		Alert,
		Button,
		Container,
		Icon,
		ListItem,
		Modal,
		PlainTable,
		Text,
	} from '@Shelly/shelly-design-system';
	// Helpers
	import {
		formatSelectraFeatureValue,
		getSortedSelectraSupplyFeatures,
		getUniqueNonEmptyValues,
		resolveSelectraOfferName,
	} from 'src/helpers/selectra_details_helpers';
	import {
		formatSelectraPeriodDate,
		getActiveSelectraPeriod,
	} from 'src/helpers/selectra_tariff_helpers';

	let showWizard = false;
	let showRemoveConfirm = false;
	let removing = false;

	const getLocalizedName = (value: unknown): string => {
		if (typeof value === 'string') return value;

		if (value && typeof value === 'object') {
			const localized = value as Record<string, string>;
			const values = Object.values(localized);
			return values[0] ?? '';
		}

		return '';
	};

	$: offerName = getLocalizedName($selectraQualification?.offer_name);
	$: optionName =
		typeof $selectraQualification?.option_name === 'string'
			? $selectraQualification.option_name
			: '';
	$: distributorName =
		typeof $selectraQualification?.distributor_name === 'string'
			? $selectraQualification.distributor_name
			: '';
	$: detailsOfferName = resolveSelectraOfferName(
		$selectraDetails?.offer?.name,
		$selectraDetails?.country_code ?? '',
	);
	$: detailsSubtitleParts = $selectraDetails
		? getUniqueNonEmptyValues([
				$selectraDetails?.offer?.provider?.name ?? '',
				$selectraDetails?.option?.name ?? '',
				$selectraDetails?.offer?.type ?? '',
			])
		: [];
	$: qualificationSubtitleParts = getUniqueNonEmptyValues([optionName, distributorName]);
	$: planTitle =
		detailsOfferName ||
		offerName ||
		optionName ||
		$format('label_selectra_offer', { default: 'Dynamic energy plan' });
	$: planSubtitle = (
		detailsSubtitleParts.length ? detailsSubtitleParts : qualificationSubtitleParts
	).join(' · ');
	$: providerLogo = $selectraDetails?.offer?.provider?.logo?.trim() ?? '';
	$: pricingFeatures = $selectraDetails?.supply?.features
		? getSortedSelectraSupplyFeatures($selectraDetails.supply.features)
		: [];
	$: currentPrice = getActiveSelectraPeriod($selectraPrices);
	$: currentPriceUnit =
		typeof $selectraPrices?.currency === 'string' && $selectraPrices.currency.trim()
			? `${$selectraPrices.currency.trim()}/kWh`
			: '';
	$: hasPricingDetails = Boolean(currentPrice) || pricingFeatures.length > 0;

	const openWizard = () => {
		showWizard = true;
	};

	const confirmRemove = async () => {
		removing = true;
		await removeSelectraQualification();
		removing = false;
		showRemoveConfirm = false;
	};
</script>

{#if $selectraIsQualified}
	<ListItem
		uniqueId="selectra-plan"
		viewType="default"
		title={planTitle}
		subtitle={planSubtitle}
		image={providerLogo || undefined}
		icon={providerLogo ? undefined : 'fas fa-bolt'}
		rightIcon="fas fa-pen"
		on:click={openWizard}
	/>

	<div class="d-flex justify-content-end mt-s">
		<Button
			variant="secondary"
			class="mr-s"
			size="m"
			on:click={() => (showRemoveConfirm = true)}
			aria-label={$format('button_remove_selectra_plan', { default: 'Remove plan' })}
		>
			{$format('button_remove_selectra_plan', { default: 'Remove plan' })}
		</Button>
		<Button
			size="m"
			on:click={openWizard}
			aria-label={$format('button_reconfigure_selectra_plan', { default: 'Reconfigure' })}
		>
			{$format('button_reconfigure_selectra_plan', { default: 'Reconfigure' })}
		</Button>
	</div>

	{#if $selectraPrices?.next_update || hasPricingDetails}
		<Container class="mt-s" type="borderSolid" padding="xs" noScroll>
			{#if hasPricingDetails}
				<PlainTable type="compact">
					<tbody>
						{#if currentPrice}
							<tr>
								<th>
									{$format('label_selectra_current_price', {
										default: 'Current price',
									})}
								</th>
								<td>
									{formatSelectraFeatureValue(currentPrice.price, currentPriceUnit)}
								</td>
							</tr>
						{/if}
						{#each pricingFeatures as feature (feature.key)}
							<tr>
								<th>{feature.name}</th>
								<td>{formatSelectraFeatureValue(feature.value, feature.unit)}</td>
							</tr>
						{/each}
					</tbody>
				</PlainTable>
			{/if}

			{#if $selectraPrices?.next_update}
				<Text styleType="body-small" class={hasPricingDetails ? 'text-200 mt-s' : 'text-200'}>
					{$format('label_selectra_next_update', {
						default: 'Next update: {time}',
						values: { time: formatSelectraPeriodDate($selectraPrices.next_update) },
					})}
				</Text>
			{/if}
		</Container>
	{/if}
{:else}
	<Alert
		type="info"
		heading={$format('label_selectra_not_qualified', {
			default: 'Set up dynamic energy prices to automatically track your electricity plan.',
		})}
	/>
	<Button
		class="mt-s"
		size="m"
		on:click={openWizard}
		aria-label={$format('button_setup_selectra', { default: 'Set up' })}
	>
		{$format('button_setup_selectra', { default: 'Set up' })}
	</Button>
{/if}

{#if showWizard}
	<SelectraQualificationWizard bind:showModal={showWizard} on:close={() => (showWizard = false)} />
{/if}

{#if showRemoveConfirm}
	<Modal
		showModal={showRemoveConfirm}
		on:close={() => (showRemoveConfirm = false)}
		title={$format('title_remove_selectra_plan_confirm', {
			default: 'Remove your dynamic energy plan?',
		})}
	>
		<Container slot="content" padding="0">
			<div class="d-flex flex-direction-column align-items-center">
				<Icon size="xxl" icon="fa-light fa-trash" class="text-danger mb-xs" />
				<Text class="text-bold text-center mb-s" styleType="controls-large">
					{planTitle}
				</Text>
				<Text class="width-full">
					{$format('desc_remove_selectra_plan_confirm', {
						default:
							'This will disconnect your account from the dynamic energy prices provider. You can set it up again at any time.',
					})}
				</Text>
			</div>
		</Container>

		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{ sm: 'm' }}
				variant="secondary"
				disabled={removing}
				on:click={() => (showRemoveConfirm = false)}
				aria-label={$format('button_cancel', { default: 'Cancel' })}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{ sm: 'm' }}
				variant="danger"
				loading={removing}
				on:click={confirmRemove}
				aria-label={$format('button_remove_selectra_plan', { default: 'Remove plan' })}
			>
				{$format('button_remove_selectra_plan', { default: 'Remove plan' })}
			</Button>
		</div>
	</Modal>
{/if}
